import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { DEFAULT_PLACEMARK_COLOR, PLACEMARK_DEFAULT_ICON } from '../components/PlacemarkTool/types';
import { usePickTerrainPosition, useConstraintCheck } from './common/useCesiumInteraction';
import { useCustomDataSource } from './common/useCustomDataSource';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { generateId } from '../utils/idGeneration';
import { useVisibilityControl } from './useVisibilityControl';
import { useLocalStoragePersistence } from './useLocalStoragePersistence';
import { useEventSubscribe } from '../events/hooks';
import { elevationService } from '../services/elevationService';
import { createPlacemarkPin, createPlacemarkLabel, updateBillboardOpacity, updateLabelOpacity } from '../utils/cesiumEntityFactory';
import { placemarkLabelOffsetY } from '../utils/placemarkIcons/placemarkIconRenderer';
import { STORAGE_KEY, serializePlacemarksForStorage, deserializeStoredPlacemarks } from './placemarks/placemarkSerialization';
const generatePlacemarkId = () => generateId('placemark');
export const usePlacemarks = ({ viewer, isActive, projectId, constraintPolygon }) => {
    const [placemarks, setPlacemarks] = useState(new Map());
    const [selectedPlacemark, setSelectedPlacemark] = useState(null);
    const [hoveredPlacemark, setHoveredPlacemark] = useState(null);
    const [placemarkCounter, setPlacemarkCounter] = useState(1);
    const [dragging, setDragging] = useState(null);
    const [itemOpacities, setItemOpacities] = useState(new Map());
    const [selectedElevation, setSelectedElevation] = useState(undefined);
    const dataSourceRef = useCustomDataSource(viewer, 'tool-placemarks');
    // Refs for handler stability
    const placemarksRef = useRef(placemarks);
    const draggingRef = useRef(null);
    const pickTerrainPositionRef = useRef(() => null);
    const isPositionInConstraintRef = useRef(() => true);
    useEffect(() => { placemarksRef.current = placemarks; }, [placemarks]);
    useEffect(() => { draggingRef.current = dragging; }, [dragging]);
    const getPlacemarkEntities = useCallback((p) => {
        return [p.entity, p.labelEntity];
    }, []);
    const { isVisible, setIsVisible, hideAll, showAll, toggleItemVisibility, isItemVisible, hideEntities } = useVisibilityControl(placemarks, getPlacemarkEntities);
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.removeAll();
            }
            setPlacemarks(new Map());
            setSelectedPlacemark(null);
            setHoveredPlacemark(null);
            setPlacemarkCounter(1);
        }
        prevProjectIdRef.current = projectId;
    }, [projectId]);
    const createPlacemarkEntity = useCallback((position, color, name, icon, showLabel = true, scale = 1.0) => {
        if (!dataSourceRef.current)
            return { pinEntity: null, labelEntity: null };
        const pinEntity = createPlacemarkPin(dataSourceRef.current, position, color, icon, 1.0, viewer !== null && viewer !== void 0 ? viewer : undefined, scale);
        const labelEntity = createPlacemarkLabel(dataSourceRef.current, position, name, 1.0, scale);
        if (labelEntity)
            labelEntity.show = showLabel;
        return { pinEntity, labelEntity };
    }, [viewer]);
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    const isPositionInConstraint = useConstraintCheck(constraintPolygon);
    useEffect(() => { pickTerrainPositionRef.current = pickTerrainPosition; }, [pickTerrainPosition]);
    useEffect(() => { isPositionInConstraintRef.current = isPositionInConstraint; }, [isPositionInConstraint]);
    const checkExistingPlacemarkClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            for (const [placemarkId, placemark] of placemarks.entries()) {
                if (pickedObject.id === placemark.entity || pickedObject.id === placemark.labelEntity) {
                    setSelectedPlacemark({
                        placemarkId,
                        screenPosition: { x: event.position.x, y: event.position.y }
                    });
                    return true;
                }
            }
        }
        return false;
    }, [viewer, placemarks]);
    const handleMapMouseDown = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [id, placemark] of placemarks.entries()) {
                if (entity === placemark.entity || entity === placemark.labelEntity) {
                    setDragging({ placemarkId: id });
                    disableCameraControls(viewer);
                    return true;
                }
            }
        }
        return false;
    }, [viewer, placemarks]);
    const handleMapMouseUp = useCallback(() => {
        if (dragging && viewer && !viewer.isDestroyed()) {
            enableCameraControls(viewer);
            setDragging(null);
            return true;
        }
        return false;
    }, [dragging, viewer]);
    const handleMapClick = useCallback((event) => {
        if (!isActive)
            return false;
        if (dragging)
            return false;
        if (checkExistingPlacemarkClick(event)) {
            return true;
        }
        const position = pickTerrainPosition(event.position);
        if (!position)
            return false;
        if (!isPositionInConstraint(position))
            return false;
        const id = generatePlacemarkId();
        const name = `Placemark ${placemarkCounter}`;
        const color = DEFAULT_PLACEMARK_COLOR;
        const icon = PLACEMARK_DEFAULT_ICON;
        const { pinEntity, labelEntity } = createPlacemarkEntity(position, color, name, icon);
        const newPlacemark = {
            id,
            name,
            position,
            color,
            icon,
            projectId: projectId || undefined,
            entity: pinEntity || undefined,
            labelEntity: labelEntity || undefined
        };
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, newPlacemark);
            return newMap;
        });
        setPlacemarkCounter(prev => prev + 1);
        setSelectedPlacemark({
            placemarkId: id,
            screenPosition: { x: event.position.x, y: event.position.y }
        });
        return true;
    }, [isActive, dragging, pickTerrainPosition, isPositionInConstraint, createPlacemarkEntity, checkExistingPlacemarkClick, placemarkCounter, projectId]);
    const handleMapHover = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const currentDragging = draggingRef.current;
        if (currentDragging) {
            const position = pickTerrainPositionRef.current(event.endPosition);
            if (position && isPositionInConstraintRef.current(position)) {
                const placemark = placemarksRef.current.get(currentDragging.placemarkId);
                if (placemark) {
                    if (placemark.entity) {
                        placemark.entity.position = new Cesium.ConstantPositionProperty(position);
                    }
                    if (placemark.labelEntity) {
                        placemark.labelEntity.position = new Cesium.ConstantPositionProperty(position);
                    }
                    setPlacemarks(prev => {
                        const newMap = new Map(prev);
                        newMap.set(currentDragging.placemarkId, { ...placemark, position });
                        return newMap;
                    });
                }
            }
            return true;
        }
        const pickedObject = viewer.scene.pick(event.endPosition);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            for (const [placemarkId, placemark] of placemarksRef.current.entries()) {
                if (pickedObject.id === placemark.entity || pickedObject.id === placemark.labelEntity) {
                    setHoveredPlacemark({
                        placemarkId,
                        screenPosition: { x: event.endPosition.x, y: event.endPosition.y }
                    });
                    return true;
                }
            }
        }
        setHoveredPlacemark(null);
        return false;
    }, [viewer]);
    const addPlacemark = useCallback((latitude, longitude, name, color, icon, showLabel, scale) => {
        const id = generatePlacemarkId();
        const placemarkName = name || `Placemark ${placemarkCounter}`;
        const placemarkColor = color || DEFAULT_PLACEMARK_COLOR;
        const placemarkIcon = icon || PLACEMARK_DEFAULT_ICON;
        const position = Cesium.Cartesian3.fromDegrees(longitude, latitude);
        const labelVisible = showLabel !== null && showLabel !== void 0 ? showLabel : true;
        const placemarkScale = scale !== null && scale !== void 0 ? scale : 1.0;
        const { pinEntity, labelEntity } = createPlacemarkEntity(position, placemarkColor, placemarkName, placemarkIcon, labelVisible, placemarkScale);
        const newPlacemark = {
            id,
            name: placemarkName,
            position,
            color: placemarkColor,
            icon: placemarkIcon,
            showLabel,
            scale,
            projectId: projectId || undefined,
            entity: pinEntity || undefined,
            labelEntity: labelEntity || undefined
        };
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, newPlacemark);
            return newMap;
        });
        if (!name) {
            setPlacemarkCounter(prev => prev + 1);
        }
        return id;
    }, [createPlacemarkEntity, placemarkCounter, projectId]);
    const removePlacemark = useCallback((id) => {
        const placemark = placemarks.get(id);
        if (!placemark)
            return;
        if (dataSourceRef.current) {
            if (placemark.entity) {
                dataSourceRef.current.entities.remove(placemark.entity);
            }
            if (placemark.labelEntity) {
                dataSourceRef.current.entities.remove(placemark.labelEntity);
            }
        }
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });
        if ((selectedPlacemark === null || selectedPlacemark === void 0 ? void 0 : selectedPlacemark.placemarkId) === id) {
            setSelectedPlacemark(null);
        }
    }, [placemarks, selectedPlacemark]);
    const updatePlacemarkColor = useCallback((id, color) => {
        var _a, _b;
        const placemark = placemarks.get(id);
        if (!placemark || !dataSourceRef.current)
            return;
        if (placemark.entity) {
            dataSourceRef.current.entities.remove(placemark.entity);
        }
        if (placemark.labelEntity) {
            dataSourceRef.current.entities.remove(placemark.labelEntity);
        }
        const icon = placemark.icon || PLACEMARK_DEFAULT_ICON;
        const labelVisible = (_a = placemark.showLabel) !== null && _a !== void 0 ? _a : true;
        const scaleValue = (_b = placemark.scale) !== null && _b !== void 0 ? _b : 1.0;
        const { pinEntity, labelEntity } = createPlacemarkEntity(placemark.position, color, placemark.name, icon, labelVisible, scaleValue);
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, {
                ...placemark,
                color,
                entity: pinEntity || undefined,
                labelEntity: labelEntity || undefined
            });
            return newMap;
        });
    }, [placemarks, createPlacemarkEntity]);
    const updatePlacemarkIcon = useCallback((id, icon) => {
        var _a, _b;
        const placemark = placemarks.get(id);
        if (!placemark || !dataSourceRef.current)
            return;
        if (placemark.entity) {
            dataSourceRef.current.entities.remove(placemark.entity);
        }
        if (placemark.labelEntity) {
            dataSourceRef.current.entities.remove(placemark.labelEntity);
        }
        const labelVisible = (_a = placemark.showLabel) !== null && _a !== void 0 ? _a : true;
        const scaleValue = (_b = placemark.scale) !== null && _b !== void 0 ? _b : 1.0;
        const { pinEntity, labelEntity } = createPlacemarkEntity(placemark.position, placemark.color, placemark.name, icon, labelVisible, scaleValue);
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, {
                ...placemark,
                icon,
                entity: pinEntity || undefined,
                labelEntity: labelEntity || undefined
            });
            return newMap;
        });
    }, [placemarks, createPlacemarkEntity]);
    // Live scale update — mutates billboard.scale on the existing entity
    // (no rebuild). Cesium re-renders next frame. The label offset tracks the
    // billboard height so the name stays just above the badge at any size.
    const updatePlacemarkScale = useCallback((id, scale) => {
        var _a, _b;
        const placemark = placemarks.get(id);
        if (!placemark)
            return;
        if ((_a = placemark.entity) === null || _a === void 0 ? void 0 : _a.billboard) {
            placemark.entity.billboard.scale = new Cesium.ConstantProperty(scale);
        }
        if ((_b = placemark.labelEntity) === null || _b === void 0 ? void 0 : _b.label) {
            placemark.labelEntity.label.pixelOffset = new Cesium.ConstantProperty(new Cesium.Cartesian2(0, placemarkLabelOffsetY(scale)));
        }
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, { ...placemark, scale });
            return newMap;
        });
    }, [placemarks]);
    // Toggle the on-map label without rebuilding entities — just flips
    // entity.show. State stores undefined for default-on so the saved
    // artifact stays NULL in the DB when the user never touches the
    // toggle (preserving "default behavior" semantics).
    const updatePlacemarkShowLabel = useCallback((id, showLabel) => {
        const placemark = placemarks.get(id);
        if (!placemark)
            return;
        if (placemark.labelEntity) {
            placemark.labelEntity.show = showLabel;
        }
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, { ...placemark, showLabel });
            return newMap;
        });
    }, [placemarks]);
    const updatePlacemarkOpacity = useCallback((id, opacity) => {
        const placemark = placemarks.get(id);
        if (!placemark)
            return;
        if (placemark.entity) {
            updateBillboardOpacity(placemark.entity, placemark.color, opacity, placemark.icon, viewer !== null && viewer !== void 0 ? viewer : undefined);
        }
        if (placemark.labelEntity) {
            updateLabelOpacity(placemark.labelEntity, opacity);
        }
        setItemOpacities(prev => {
            const newMap = new Map(prev);
            newMap.set(id, opacity);
            return newMap;
        });
    }, [placemarks, viewer]);
    const getItemOpacity = (id) => {
        var _a;
        return (_a = itemOpacities.get(id)) !== null && _a !== void 0 ? _a : 1;
    };
    const updatePlacemarkName = useCallback((id, name) => {
        const placemark = placemarks.get(id);
        if (!placemark)
            return;
        if (placemark.labelEntity) {
            const label = placemark.labelEntity.label;
            if (label) {
                label.text = new Cesium.ConstantProperty(name);
            }
        }
        setPlacemarks(prev => {
            const newMap = new Map(prev);
            newMap.set(id, {
                ...placemark,
                name
            });
            return newMap;
        });
    }, [placemarks]);
    const clearAllPlacemarks = useCallback(() => {
        if (dataSourceRef.current) {
            placemarks.forEach(placemark => {
                if (placemark.entity) {
                    dataSourceRef.current.entities.remove(placemark.entity);
                }
                if (placemark.labelEntity) {
                    dataSourceRef.current.entities.remove(placemark.labelEntity);
                }
            });
        }
        setPlacemarks(new Map());
        setSelectedPlacemark(null);
        setHoveredPlacemark(null);
        setPlacemarkCounter(1);
    }, [placemarks]);
    const dismissSelection = useCallback(() => {
        setSelectedPlacemark(null);
    }, []);
    const selectPlacemarkById = useCallback((placemarkId, screenPosition) => {
        // No existence check: caller may invoke right after a state-updating create.
        setSelectedPlacemark({
            placemarkId,
            screenPosition: screenPosition || { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, []);
    const restoreFromConfig = useCallback((configs, startHidden = false) => {
        if (!dataSourceRef.current || !configs || configs.length === 0)
            return;
        configs.forEach(config => {
            var _a, _b;
            const position = Cesium.Cartesian3.fromDegrees(config.longitude, config.latitude);
            const icon = config.icon || PLACEMARK_DEFAULT_ICON;
            const labelVisible = (_a = config.showLabel) !== null && _a !== void 0 ? _a : true;
            const scaleValue = (_b = config.scale) !== null && _b !== void 0 ? _b : 1.0;
            const { pinEntity, labelEntity } = createPlacemarkEntity(position, config.color, config.name, icon, labelVisible, scaleValue);
            if (startHidden) {
                if (pinEntity)
                    pinEntity.show = false;
                if (labelEntity)
                    labelEntity.show = false;
            }
            const placemark = {
                id: config.id,
                name: config.name,
                position,
                color: config.color,
                icon,
                showLabel: config.showLabel,
                scale: config.scale,
                projectId: config.projectId,
                entity: pinEntity || undefined,
                labelEntity: labelEntity || undefined
            };
            setPlacemarks(prev => {
                const newMap = new Map(prev);
                newMap.set(config.id, placemark);
                return newMap;
            });
        });
    }, [createPlacemarkEntity]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && selectedPlacemark) {
                setSelectedPlacemark(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedPlacemark]);
    useEffect(() => {
        if (selectedPlacemark && !isItemVisible(selectedPlacemark.placemarkId)) {
            setSelectedPlacemark(null);
        }
    }, [selectedPlacemark, isItemVisible]);
    useEffect(() => {
        if (!selectedPlacemark || !projectId) {
            setSelectedElevation(undefined);
            return;
        }
        const placemark = placemarks.get(selectedPlacemark.placemarkId);
        if (!placemark) {
            setSelectedElevation(undefined);
            return;
        }
        let cancelled = false;
        setSelectedElevation(undefined);
        const cart = Cesium.Cartographic.fromCartesian(placemark.position);
        const lat = Cesium.Math.toDegrees(cart.latitude);
        const lng = Cesium.Math.toDegrees(cart.longitude);
        elevationService.sampleElevation(projectId, lat, lng)
            .then(elev => {
            if (!cancelled)
                setSelectedElevation(elev);
        })
            .catch(() => {
            if (!cancelled)
                setSelectedElevation(null);
        });
        return () => { cancelled = true; };
    }, [selectedPlacemark === null || selectedPlacemark === void 0 ? void 0 : selectedPlacemark.placemarkId, projectId, placemarks]);
    const serializeForStorage = useCallback(() => {
        const existingJson = localStorage.getItem(STORAGE_KEY);
        return serializePlacemarksForStorage(placemarks, projectId, existingJson);
    }, [placemarks, projectId]);
    const restoreFromStorage = useCallback((stored) => {
        if (!projectId)
            return;
        const configs = deserializeStoredPlacemarks(stored, projectId);
        restoreFromConfig(configs, true);
    }, [restoreFromConfig, projectId]);
    const { saveToStorage } = useLocalStoragePersistence({
        storageKey: STORAGE_KEY,
        serialize: serializeForStorage,
        restore: restoreFromStorage,
        isReady: !!dataSourceRef.current && !!projectId,
        resetKey: projectId
    });
    useEffect(() => {
        saveToStorage();
    }, [placemarks, saveToStorage]);
    // Subscribe to scope events for clear and restore operations
    useEventSubscribe('scope:clearAll', () => {
        clearAllPlacemarks();
    }, [clearAllPlacemarks]);
    useEventSubscribe('scope:restore', (payload) => {
        if (payload.placemarks && payload.placemarks.length > 0) {
            restoreFromConfig(payload.placemarks);
        }
    }, [restoreFromConfig]);
    const isEntityDraggable = useCallback((entity) => {
        for (const [, placemark] of placemarks.entries()) {
            if (entity === placemark.entity || entity === placemark.labelEntity)
                return true;
        }
        return false;
    }, [placemarks]);
    return {
        placemarks,
        selectedPlacemark,
        selectedElevation,
        hoveredPlacemark,
        isVisible,
        dragging,
        handleMapClick,
        handleMapHover,
        handleMapMouseDown,
        handleMapMouseUp,
        checkExistingPlacemarkClick,
        isEntityDraggable,
        addPlacemark,
        removePlacemark,
        updatePlacemarkColor,
        updatePlacemarkIcon,
        updatePlacemarkShowLabel,
        updatePlacemarkScale,
        updatePlacemarkOpacity,
        getItemOpacity,
        updatePlacemarkName,
        clearAllPlacemarks,
        dismissSelection,
        selectPlacemarkById,
        restoreFromConfig,
        hideAll,
        showAll,
        toggleItemVisibility,
        isItemVisible,
        placemarkCount: placemarks.size
    };
};
